import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, FileText, Image, Trash2,
  MessageCircle, CreditCard, Zap, FileDown, Upload,
  Paperclip, Download, Send, X, File
} from "lucide-react";
import { statusColors, productTypes, PRICING_DATA_FRONT, paymentStatuses } from "@/data/adminData";
import { QuotesSection } from "./QuoteModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClientFiles({ simulation }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/simulations/${simulation.id}/files`);
      setFiles(res.data);
    } catch {}
  };

  useEffect(() => { fetchFiles(); }, [simulation.id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/simulations/${simulation.id}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`"${file.name}" uploadé`);
      fetchFiles();
    } catch { toast.error("Erreur lors de l'upload"); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deleteFile = async (fileId, filename) => {
    if (!window.confirm(`Supprimer "${filename}" ?`)) return;
    try {
      await axios.delete(`${API}/client-files/${fileId}`);
      toast.success("Fichier supprimé");
      fetchFiles();
    } catch { toast.error("Erreur"); }
  };

  const sendViaWhatsApp = (file) => {
    const downloadUrl = `${BACKEND_URL}${file.download_url}`;
    const phone = simulation.phone.replace(/\s+/g, '').replace(/^0/, '33');
    const msg = encodeURIComponent(
      `Bonjour ${simulation.name},\n\n` +
      `Voici votre document : ${file.filename}\n` +
      `${downloadUrl}\n\n` +
      `PUB PRO - simulation-enseigne.fr`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="pt-4 border-t border-[#262626]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#A3A3A3] flex items-center gap-2">
          <Paperclip className="w-4 h-4" />
          Fichiers client ({files.length})
        </p>
        <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${uploading ? 'bg-[#262626] text-[#666]' : 'bg-[#EFFF00]/10 text-[#EFFF00] hover:bg-[#EFFF00]/20'}`}>
          <Upload className="w-3.5 h-3.5" />
          {uploading ? 'Upload...' : 'Ajouter'}
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt" data-testid="client-file-upload" />
        </label>
      </div>

      {files.length > 0 && (
        <div className="space-y-2" data-testid="client-files-list">
          {files.map((f) => (
            <div key={f.file_id} className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#1a1a1a] flex items-center justify-center shrink-0">
                  <File className="w-4 h-4 text-[#EFFF00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{f.filename}</p>
                  <p className="text-[#666] text-[10px]">{formatSize(f.size)} — {new Date(f.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              <div className="flex gap-1.5 mt-2">
                <button
                  onClick={() => sendViaWhatsApp(f)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#25D366]/10 text-[#25D366] text-xs font-medium hover:bg-[#25D366]/20 transition-colors"
                  data-testid={`send-file-whatsapp-${f.file_id}`}
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  WhatsApp
                </button>
                <a
                  href={`${BACKEND_URL}${f.download_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1a1a1a] text-[#A3A3A3] text-xs hover:text-white transition-colors"
                  data-testid={`download-file-${f.file_id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => deleteFile(f.file_id, f.filename)}
                  className="flex items-center justify-center px-2 py-1.5 rounded-lg text-[#666] hover:text-red-400 transition-colors"
                  data-testid={`delete-file-${f.file_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export function LeadDetails({ simulation, onStatusUpdate, onDelete, onOpenQuoteModal, onRefresh }) {
  if (!simulation) {
    return (
      <div className="bg-[#121212] border border-[#262626] rounded-xl p-6 text-center text-[#A3A3A3]">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Sélectionnez une demande pour voir les détails</p>
      </div>
    );
  }

  return (
    <div className="bg-[#121212] border border-[#262626] rounded-xl p-6 sticky top-6">
      <h3 className="text-lg font-bold mb-4">{simulation.name}</h3>
      
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Phone className="w-4 h-4 text-[#EFFF00]" />
          <a href={`tel:${simulation.phone}`} className="text-white hover:text-[#EFFF00]">{simulation.phone}</a>
        </div>
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-[#EFFF00]" />
          <a href={`mailto:${simulation.email}`} className="text-white hover:text-[#EFFF00] break-all">{simulation.email}</a>
        </div>
        <div className="flex items-center gap-3">
          <MapPin className="w-4 h-4 text-[#EFFF00]" />
          <span>{simulation.city}</span>
        </div>
        
        {simulation.project_description && (
          <div className="pt-4 border-t border-[#262626]">
            <p className="text-sm text-[#A3A3A3] mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Description</p>
            <p className="text-white text-sm">{simulation.project_description}</p>
          </div>
        )}
        
        {simulation.photo_url && (
          <div className="pt-4 border-t border-[#262626]">
            <p className="text-sm text-[#A3A3A3] mb-2 flex items-center gap-2"><Image className="w-4 h-4" /> Photo de façade</p>
            <img src={simulation.photo_url} alt="Façade" className="w-full rounded-lg" />
          </div>
        )}
        
        <div className="pt-4 border-t border-[#262626]">
          <p className="text-sm text-[#A3A3A3] mb-3">Changer le statut</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(statusColors).map(([status, config]) => (
              <button
                key={status}
                onClick={() => onStatusUpdate(simulation.id, status)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  simulation.status === status
                    ? `${config.bg} ${config.text} ring-2 ring-offset-2 ring-offset-[#121212] ring-current`
                    : 'bg-[#1a1a1a] text-[#A3A3A3] hover:bg-[#262626]'
                }`}
                data-testid={`status-btn-${status}`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>

        {(simulation.status === "signed" || simulation.status === "quoted" || simulation.payment_status) && (
          <div className="pt-4 border-t border-[#262626]">
            <p className="text-sm text-[#A3A3A3] mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Suivi Paiement
            </p>
            <div className="grid grid-cols-3 gap-2">
              {paymentStatuses.map(ps => (
                <button
                  key={ps.value}
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/simulations/${simulation.id}/payment`, { payment_status: ps.value });
                      toast.success(ps.value === "acompte_received" ? "Acompte confirmé !" : ps.value === "paid" ? "Paiement soldé !" : "Statut mis à jour");
                      onRefresh();
                    } catch { toast.error("Erreur"); }
                  }}
                  className={`px-2 py-2.5 rounded-lg text-xs font-medium transition-all ring-offset-2 ring-offset-[#121212] ${
                    (simulation.payment_status || "pending") === ps.value ? ps.active : ps.color + ' hover:bg-[#262626]'
                  }`}
                  data-testid={`payment-btn-${ps.value}`}
                >
                  {ps.label}
                </button>
              ))}
            </div>
            {simulation.payment_date && (
              <p className="text-[#444] text-xs mt-2">
                Dernière maj: {new Date(simulation.payment_date).toLocaleDateString('fr-FR')}
              </p>
            )}
          </div>
        )}
        
        {/* Client Files Section */}
        <ClientFiles simulation={simulation} />

        <QuotesSection 
          simulationId={simulation.id} 
          onEditQuote={(q) => {
            onOpenQuoteModal({
              product_type: q.product_type || "enseigne_lumineuse",
              estimated_price: q.estimated_price || 2500,
              description: q.description || ""
            });
          }}
        />

        {simulation.ai_suggestion && (
          <div className="pt-4 border-t border-[#262626]">
            <p className="text-sm text-[#A3A3A3] mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#EFFF00]" /> Suggestion IA
            </p>
            <div className="bg-[#0A0A0A] border border-[#EFFF00]/20 rounded-lg p-4 space-y-2">
              <p className="text-xs text-[#EFFF00] font-bold">
                {PRICING_DATA_FRONT[simulation.ai_suggestion.product_type] || simulation.ai_suggestion.product_type}
              </p>
              <p className="text-white text-sm font-bold">
                {simulation.ai_suggestion.estimated_price}€ HT
                <span className="text-[#666] font-normal ml-2">(après remise: {(simulation.ai_suggestion.estimated_price - 500)}€ HT)</span>
              </p>
              <p className="text-[#A3A3A3] text-xs whitespace-pre-line leading-relaxed">
                {simulation.ai_suggestion.description?.substring(0, 200)}...
              </p>
              <Button
                onClick={() => onOpenQuoteModal({
                  product_type: simulation.ai_suggestion.product_type,
                  estimated_price: simulation.ai_suggestion.estimated_price,
                  description: simulation.ai_suggestion.description || ""
                })}
                className="w-full mt-2 bg-[#EFFF00]/10 text-[#EFFF00] hover:bg-[#EFFF00]/20 border border-[#EFFF00]/30"
                data-testid="use-ai-suggestion-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Utiliser cette suggestion
              </Button>
            </div>
          </div>
        )}
        
        <div className="pt-4 border-t border-[#262626] space-y-2">
          <Button
            onClick={() => {
              const defaultType = productTypes.find(t => t.value === "enseigne_lumineuse");
              onOpenQuoteModal({
                product_type: "enseigne_lumineuse",
                estimated_price: defaultType.price,
                description: defaultType.desc
              });
            }}
            className="w-full bg-[#EFFF00] text-black hover:bg-[#d4e000]"
            data-testid="generate-quote-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Générer un devis PDF
          </Button>
          
          <div className="flex gap-2">
            <a
              href={`https://wa.me/33${simulation.phone.replace(/^0/, '')}?text=Bonjour ${simulation.name}, suite à votre demande de simulation...`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </a>
            <Button
              onClick={() => onDelete(simulation.id)}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
              data-testid="delete-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
